import axios from "axios";
import AuthenticationFailureHandler from "../utils/AuthenticationFailureHandler";
import {
  REPORTING_BASE_URL,
  RESERVATION_BASE_URL,
  IS_CANADA_MARKET,
  BASE_URL,
} from "../utils/EmpUtil";
import { is401 } from "../errors/ErrorConstants";

function handleError(error) {
  if (is401(error)) {
    AuthenticationFailureHandler.execute();
  }

  return Promise.reject(error);
}

function normalizeDateFields(reqData) {
  if (reqData.dateFrom === "") {
    reqData.dateFrom = null;
  }
  if (reqData.dateTo === "") {
    reqData.dateTo = null;
  }
  if (reqData.dateRange === "") {
    reqData.dateRange = null;
  }
}

export default class DealerReportingClient {
  static baseUrl = REPORTING_BASE_URL;
  static naBaseUrl = BASE_URL;

  static downloadOrderStatusReportInCsv(user, lang) {
    const commonIds = user.getCommonIdsWithSDLR
      ? user.getCommonIdsWithSDLR().split(",")
      : user.commonId.split("|").join("%7C");
    const url = `${RESERVATION_BASE_URL}/dealer/order-status-report?dealerCommonId=${commonIds}&lang=${lang}`;
    return axios
      .get(url)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        return handleError(error);
      });
  }

  static getCustomerNames(user, orderIds) {
    const url = `${this.baseUrl}/reporting/getCustomerNames?orderIds=${orderIds}`;
    return axios.get(url);
  }

  static getFieldReport(user, reqData) {
    const url = `${this.baseUrl}/reporting/${user.market}/field-report`;

    normalizeDateFields(reqData);

    return axios
      .post(url, reqData)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        return handleError(error);
      });
  }

  static downloadPurchaseRequestReportInCsv(
    user,
    lang,
    commonId,
    withTransactionData,
    withTradeInData,
    reqData,
    fieldViewDealer
  ) {
    const includeTransactionData =
      withTransactionData === undefined ? true : withTransactionData;
    const includeTradeInData =
      withTradeInData === undefined ? true : withTradeInData;
    const isFieldViewDealer =
      fieldViewDealer === undefined ? false : fieldViewDealer;

    const userType = isFieldViewDealer ? "na_dealer" : user.userType;
    const url = `${this.baseUrl}/reporting/${user.market}/purchase-requests-report`;
    reqData.userType = userType;
    reqData.marketId = user.market;
    reqData.commonIds = user.commonDealerId;
    reqData.withTransactionData = includeTransactionData;
    reqData.withTradeInData = includeTradeInData;

    normalizeDateFields(reqData);

    return axios
      .post(url, reqData)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        return handleError(error);
      });
  }

  static downloadModelEReportInCsv(
    user,
    lang,
    commonId,
    withTransactionData,
    withTradeInData,
    reqData,
    fieldViewDealer
  ) {
    const includeTransactionData =
      withTransactionData === undefined ? true : withTransactionData;
    const includeTradeInData =
      withTradeInData === undefined ? true : withTradeInData;
    const isFieldViewDealer =
      fieldViewDealer === undefined ? false : fieldViewDealer;

    const userType = isFieldViewDealer ? "na_dealer" : user.userType;
    const url = `${this.baseUrl}/reporting/${user.market}/modelE-report`;
    reqData.userType = userType;
    reqData.marketId = user.market;
    reqData.commonIds = user.commonDealerId;
    reqData.withTransactionData = includeTransactionData;
    reqData.withTradeInData = includeTradeInData;

    normalizeDateFields(reqData);

    return axios
      .post(url, reqData)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        return handleError(error);
      });
  }

  static downloadDdtUtilizationReportInCsv(user, reqData) {
    const url = `${this.naBaseUrl}/dealer/ddt-utilization-report?dealerPACode=${user.dealerId}&salesCode=${user.salesCode}`;

    normalizeDateFields(reqData);

    console.log("[DealerReportingClient] URL for DDT Utilization Report:", url);
    console.log(
      "[DealerReportingClient] Request Data for DDT Utilization Report:",
      reqData
    );

    return axios
      .post(url, reqData)
      .then((response) => {
        console.log(
          "[DealerReportingClient] Response Data for DDT Utilization Report:",
          response.data
        );
        return response.data;
      })
      .catch((error) => {
        console.log(
          "[DealerReportingClient] Error while fetching DDT Utilization Report:",
          error
        );
        return handleError(error);
      });
  }

  static downloadDealerReservationReportInCsv(
    user,
    lang,
    commonId,
    withTransactionData,
    reqData,
    fieldViewDealer
  ) {
    const includeTransactionData =
      withTransactionData === undefined ? true : withTransactionData;
    const isFieldViewDealer =
      fieldViewDealer === undefined ? false : fieldViewDealer;

    const userType =
      isFieldViewDealer && !IS_CANADA_MARKET(user.market)
        ? "na_dealer"
        : user.userType;
    const url = `${this.baseUrl}/reporting/${user.market}/reservations-report`;
    reqData.userType = userType;
    reqData.marketId = user.market;
    reqData.commonIds = user.commonDealerId;
    reqData.withTransactionData = includeTransactionData;

    if (IS_CANADA_MARKET(user.market)) {
      reqData.orderType = "RESERVATION";
    }
    normalizeDateFields(reqData);

    return axios
      .post(url, reqData)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        return handleError(error);
      });
  }

  static downloadOrderConversionReportInCsv(
    user,
    lang,
    commonId,
    withTransactionData,
    reqData,
    fieldViewDealer
  ) {
    const includeTransactionData =
      withTransactionData === undefined ? true : withTransactionData;
    const isFieldViewDealer =
      fieldViewDealer === undefined ? false : fieldViewDealer;

    const userType = isFieldViewDealer ? "na_dealer" : user.userType;
    const url = `${this.baseUrl}/reporting/${user.market}/orders-report`;
    reqData.userType = userType;
    reqData.marketId = user.market;
    reqData.commonIds = user.commonDealerId;
    reqData.withTransactionData = includeTransactionData;

    normalizeDateFields(reqData);

    return axios
      .post(url, reqData)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        return handleError(error);
      });
  }

  static downloadOpenTransactionsReportInCsv(
    user,
    lang,
    commonId,
    reqData,
    withTransactionData = true,
    fieldViewDealer = false
  ) {
    const userType = fieldViewDealer ? "na_dealer" : user.userType;
    const url = `${this.baseUrl}/reporting/${user.market}/open-transactions-report`;
    reqData.userType = userType;
    reqData.marketId = user.market;
    reqData.commonIds = user.commonDealerId;
    reqData.withTransactionData = withTransactionData;

    normalizeDateFields(reqData);

    return axios
      .post(url, reqData)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        return handleError(error);
      });
  }
}
